import React, { useEffect, useState } from "react";
import {
  fetchBlocks, fetchBlock, createBlock, addBlockRoom, removeBlockRoom, updateBlock,
  blockPayment, cancelBlock, blockCheckIn, blockCheckOut, fetchBlockReceipt, updateReservation, reinstateReservation,
  fetchRoomTypes, fetchRoomAvailability, getRole, getCurrency,
  addBlockCharge, deleteBlockCharge,
  fetchPartnerHotels, createBlockOutsource,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";
import RoomPicker from "../components/RoomPicker";

// Front Desk — Slice 2e: group / company block bookings (company pays all).
const S = {
  en: {
    title: "Group bookings", subtitle: "Company & NGO blocks — one bill for many rooms",
    tab_groups: "Groups", tab_new_group: "+ New group",
    new_group: "New group booking", name: "Company / event name", contact: "Contact person", phone: "Phone",
    company: "Company (bill to)", company_tin: "Company TIN", room_type: "Room type", pick_type: "Select room type",
    num_rooms: "Number of rooms", check_in: "Check-in", check_out: "Check-out", rate: "Group rate / night", notes: "Notes",
    create: "Create group", cancel: "Cancel", rooms: "rooms", nights: "nights",
    guest: "Guest name", room: "Room", add_room: "Add room", remove: "Remove", receipt: "Consolidated receipt",
    cancel_group: "Cancel group", cancel_group_q: "Cancel this whole group booking?",
    bulk_in: "Check in all", bulk_out: "Check out all", bulk_out_q: "Check the whole group out?",
    bulk_skipped: "{n} member(s) had no free room — assign a room, then check them in.",
    master_pay: "Company payment", method: "Method", reference: "Reference", amount: "Amount", take_pay: "Record payment",
    total: "Total", paid: "Paid", balance: "Balance",
    pm_cash: "Cash", pm_telebirr: "Telebirr", pm_cbe_birr: "CBE Birr", pm_pos: "Card/POS", pm_other: "Other",
    st_booked: "Booked", st_checked_in: "In-house", st_checked_out: "Out", st_open: "Open", st_closed: "Closed", st_cancelled: "Cancelled", st_no_show: "No-show", st_waitlist: "Waitlist", reinstate: "Reinstate",
    none: "No group bookings yet.", name_req: "Company/event name is required.", date_req: "Check-out must be after check-in.",
    could_not: "Could not save. Check the inputs and try again.", tip: "Each room appears in Reservations too — check guests in/out there.",
    room_types_h: "Room types", qty: "Qty", add_type: "Add room type", details: "Details", save: "Save", email: "Email",
    id_type: "ID type", id_kebele: "Kebele ID", id_fayda: "Fayda", id_passport: "Passport", id_other: "Other", id_number: "ID number",
    region: "Region", city: "City / Sub-city", woreda: "Woreda", kebele: "Kebele",
    origin: "From (city / country)", assign_room: "Assign room", pick_room: "Choose a room",
    av_available: "Available", av_booked: "Booked", av_occupied: "Occupied", av_ooo: "Out of order",
    room_taken: "That room was just taken — pick another.",
    no_rooms_hint: "No rooms available to assign. Add rooms under Front Desk → Rooms (or free up existing ones).",
    avail_err: "Couldn't load room availability — reopen the group or reload the page.",
    linked_events: "Linked function(s)", events_note: "Attached events are included in the totals above.",
    group_charges: "Group charges", add_group_charge: "Add group charge", gc_desc: "Description (e.g. shared buffet)", gc_add: "Add",
    group_charges_note: "Shared extras billed to the whole group — already in the total above.",
    outsource: "Outsource guest(s)", partner_hotel: "Partner hotel", sell_rate: "Sell rate/night", cost_rate: "Cost rate/night",
    margin: "Margin", outsourced_accommodation: "Outsourced accommodation", own_accommodation: "Accommodation",
    add_outsource: "Add", note: "Note", outsourced_placements: "Outsourced to partner hotels",
    partner_req: "Partner hotel is required.", rate_req: "Sell and cost rates are required.",
    edit_group: "Edit details", discount: "Discount (birr)",
  },
  am: {
    title: "የቡድን ማስያዣዎች", subtitle: "የድርጅት እና መንግስታዊ ያልሆኑ ብሎኮች — ለብዙ ክፍሎች አንድ ሂሳብ",
    tab_groups: "ቡድኖች", tab_new_group: "+ አዲስ ቡድን",
    new_group: "አዲስ የቡድን ማስያዣ", name: "የድርጅት / ዝግጅት ስም", contact: "የመገናኛ ሰው", phone: "ስልክ",
    company: "ድርጅት (የሚከፍል)", company_tin: "የድርጅት ቲን", room_type: "የክፍል ዓይነት", pick_type: "የክፍል ዓይነት ይምረጡ",
    num_rooms: "የክፍሎች ብዛት", check_in: "መግቢያ", check_out: "መውጫ", rate: "የቡድን ዋጋ / ሌሊት", notes: "ማስታወሻ",
    create: "ቡድን ፍጠር", cancel: "ሰርዝ", rooms: "ክፍሎች", nights: "ሌሊቶች",
    guest: "የእንግዳ ስም", room: "ክፍል", add_room: "ክፍል ጨምር", remove: "አስወግድ", receipt: "የተጠቃለለ ደረሰኝ",
    cancel_group: "ቡድን ሰርዝ", cancel_group_q: "ይህን ሙሉ የቡድን ማስያዣ መሰረዝ?",
    bulk_in: "ሁሉንም አስገባ", bulk_out: "ሁሉንም አስወጣ", bulk_out_q: "ሙሉ ቡድኑ ይውጣ?",
    bulk_skipped: "{n} አባል(ላት) ነፃ ክፍል አልነበራቸውም — ክፍል መድበው ያስገቡ።",
    master_pay: "የድርጅት ክፍያ", method: "ዘዴ", reference: "ማጣቀሻ", amount: "መጠን", take_pay: "ክፍያ መዝግብ",
    total: "ጠቅላላ", paid: "የተከፈለ", balance: "ቀሪ",
    pm_cash: "ጥሬ ገንዘብ", pm_telebirr: "ቴሌብር", pm_cbe_birr: "ሲቢኢ ብር", pm_pos: "ካርድ/POS", pm_other: "ሌላ",
    st_booked: "ተይዟል", st_checked_in: "በውስጥ", st_checked_out: "ወጥቷል", st_open: "ክፍት", st_closed: "ዝግ", st_cancelled: "ተሰርዟል", st_no_show: "አልመጣም", st_waitlist: "መጠባበቂያ", reinstate: "መልስ",
    none: "እስካሁን የቡድን ማስያዣ የለም።", name_req: "የድርጅት/ዝግጅት ስም ያስፈልጋል።", date_req: "መውጫ ከመግቢያ በኋላ መሆን አለበት።",
    could_not: "ማስቀመጥ አልተቻለም። ግብዓቶቹን ፈትሸው እንደገና ይሞክሩ።", tip: "እያንዳንዱ ክፍል በማስያዣዎች ውስጥም ይታያል — እንግዶችን እዚያ ያስገቡ/ያስወጡ።",
    room_types_h: "የክፍል ዓይነቶች", qty: "ብዛት", add_type: "የክፍል ዓይነት ጨምር", details: "ዝርዝሮች", save: "አስቀምጥ", email: "ኢሜይል",
    id_type: "የመታወቂያ ዓይነት", id_kebele: "የቀበሌ መታወቂያ", id_fayda: "ፋይዳ", id_passport: "ፓስፖርት", id_other: "ሌላ", id_number: "የመታወቂያ ቁጥር",
    region: "ክልል", city: "ከተማ / ክፍለ ከተማ", woreda: "ወረዳ", kebele: "ቀበሌ",
    origin: "ከየት (ከተማ / ሀገር)", assign_room: "ክፍል መድብ", pick_room: "ክፍል ይምረጡ",
    av_available: "ነፃ", av_booked: "ተይዟል", av_occupied: "ተይዞ ነው", av_ooo: "ከአገልግሎት ውጭ",
    room_taken: "ያ ክፍል አሁን ተይዟል — ሌላ ይምረጡ።",
    no_rooms_hint: "የሚመደብ ክፍል የለም። በፊት ዴስክ → ክፍሎች ውስጥ ክፍሎችን ይጨምሩ (ወይም ነባሮቹን ነፃ ያድርጉ)።",
    avail_err: "የክፍል ዝግጁነት መጫን አልተቻለም — ቡድኑን እንደገና ይክፈቱ ወይም ገጹን ያድሱ።",
    linked_events: "የተያያዙ ዝግጅቶች", events_note: "የተያያዙ ዝግጅቶች ከላይ ባለው ጠቅላላ ውስጥ ተካትተዋል።",
    group_charges: "የቡድን ክፍያዎች", add_group_charge: "የቡድን ክፍያ ጨምር", gc_desc: "መግለጫ (ለምሳሌ የጋራ ቡፌ)", gc_add: "ጨምር",
    group_charges_note: "ለጠቅላላው ቡድን የተከፈሉ የጋራ ተጨማሪዎች — ከላይ ባለው ጠቅላላ ውስጥ ተካትተዋል።",
    outsource: "እንግዶችን ወደ ውጭ ላክ", partner_hotel: "አጋር ሆቴል", sell_rate: "የመሸጫ ዋጋ/ሌሊት", cost_rate: "የወጪ ዋጋ/ሌሊት",
    margin: "ትርፍ", outsourced_accommodation: "ወደ ውጭ የተላከ መኝታ", own_accommodation: "መኝታ",
    add_outsource: "ጨምር", note: "ማስታወሻ", outsourced_placements: "ወደ አጋር ሆቴሎች የተላኩ",
    partner_req: "አጋር ሆቴል ያስፈልጋል።", rate_req: "የመሸጫ እና የወጪ ዋጋ ያስፈልጋል።",
    edit_group: "ዝርዝር አስተካክል", discount: "ቅናሽ (ብር)",
  },
  om: {
    title: "Qabannoo garee", subtitle: "Blokii dhaabbataa fi NGO — kutaalee hedduuf herrega tokko",
    tab_groups: "Garee", tab_new_group: "+ Garee haaraa",
    new_group: "Qabannoo garee haaraa", name: "Maqaa dhaabbata / sagantaa", contact: "Nama qunnamtii", phone: "Bilbila",
    company: "Dhaabbata (kaffaltu)", company_tin: "TIN dhaabbata", room_type: "Gosa kutaa", pick_type: "Gosa kutaa fili",
    num_rooms: "Baay'ina kutaalee", check_in: "Seensa", check_out: "Ba'uu", rate: "Gatii garee / halkan", notes: "Yaadannoo",
    create: "Garee uumi", cancel: "Haqi", rooms: "kutaalee", nights: "halkanoota",
    guest: "Maqaa keessummaa", room: "Kutaa", add_room: "Kutaa dabali", remove: "Haqi", receipt: "Nagahee walitti qabame",
    cancel_group: "Garee haqi", cancel_group_q: "Qabannoo garee kana guutuu haquu?",
    bulk_in: "Hunda galchi", bulk_out: "Hunda baasi", bulk_out_q: "Garee guutuu baasuu?",
    bulk_skipped: "Miseensa/miseensonni {n} kutaa duwwaa hin qaban — kutaa ramadii galchi.",
    master_pay: "Kaffaltii dhaabbata", method: "Mala", reference: "Wabii", amount: "Hanga", take_pay: "Kaffaltii galmeessi",
    total: "Ida'ama", paid: "Kaffalame", balance: "Hafe",
    pm_cash: "Maallaqa", pm_telebirr: "Telebirr", pm_cbe_birr: "CBE Birr", pm_pos: "Kaardii/POS", pm_other: "Kan biraa",
    st_booked: "Qabame", st_checked_in: "Keessa", st_checked_out: "Ba'e", st_open: "Banaa", st_closed: "Cufaa", st_cancelled: "Haqame", st_no_show: "Hin dhufne", st_waitlist: "Eeggannoo", reinstate: "Deebisi",
    none: "Ammatti qabannoon garee hin jiru.", name_req: "Maqaan dhaabbata/sagantaa barbaachisaa dha.", date_req: "Ba'uun seensa booda ta'uu qaba.",
    could_not: "Kaa'uu hin dandeenye. Irra deebi'i yaali.", tip: "Kutaan hundi Qabannoo keessattis ni mul'ata — keessummoota achitti galchi/baasi.",
    room_types_h: "Gosoota kutaa", qty: "Baay'ina", add_type: "Gosa kutaa dabali", details: "Bal'ina", save: "Kaa'i", email: "Imeelii",
    id_type: "Gosa waraqaa", id_kebele: "Waraqaa Kebele", id_fayda: "Fayda", id_passport: "Paaspoortii", id_other: "Kan biraa", id_number: "Lakk. waraqaa",
    region: "Naannoo", city: "Magaalaa / Kutaa magaalaa", woreda: "Aanaa", kebele: "Ganda",
    origin: "Eessaa (magaalaa / biyya)", assign_room: "Kutaa ramadi", pick_room: "Kutaa fili",
    av_available: "Bilisa", av_booked: "Qabame", av_occupied: "Qabamaa", av_ooo: "Hojii ala",
    room_taken: "Kutaan sun amma qabame — kan biraa fili.",
    no_rooms_hint: "Kutaan ramadamu hin jiru. Fuula Duraa → Kutaalee jalatti kutaalee dabali (yookiin kan jiran bilisa godhi).",
    avail_err: "Argama kutaa fe'uu hin dandeenye — garee irra deebi'ii bani yookiin fuula haaromsi.",
    linked_events: "Sagantaa walqabatan", events_note: "Sagantaaleen walqabatan ida'ama gubbaa keessatti hammatamaniiru.",
    group_charges: "Kaffaltii garee", add_group_charge: "Kaffaltii garee dabali", gc_desc: "Ibsa (fkn. buffet waloo)", gc_add: "Dabali",
    group_charges_note: "Dabalataalee waloo garee guutuutti kaffalaman — ida'ama gubbaa keessa jira.",
    outsource: "Keessummoota ala ergi", partner_hotel: "Hoteela michuu", sell_rate: "Gatii gurgurtaa/halkan", cost_rate: "Gatii baasii/halkan",
    margin: "Bu'aa", outsourced_accommodation: "Bakka buluu ala ergame", own_accommodation: "Bakka buluu",
    add_outsource: "Dabali", note: "Yaadannoo", outsourced_placements: "Gara hoteelota michuu ergame",
    partner_req: "Hoteelli michuu barbaachisaa dha.", rate_req: "Gatiin gurgurtaa fi baasii barbaachisaa dha.",
    edit_group: "Bal'ina gulaali", discount: "Hir'isa (birr)",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const STMETA = {
  booked: { bg: "#E6F1FB", fg: "#185FA5" }, checked_in: { bg: "#E7F6EC", fg: "#1D7A44" },
  checked_out: { bg: "#EFEFF2", fg: "#5b6472" }, open: { bg: "#E6F1FB", fg: "#185FA5" },
  closed: { bg: "#EFEFF2", fg: "#5b6472" }, cancelled: { bg: "#FCEBEB", fg: "#A32D2D" },
  no_show: { bg: "#FBF2DC", fg: "#8A6D3B" }, waitlist: { bg: "#EFEFF2", fg: "#5b6472" },
};
// Chip/pill style for the page tabs — active tab uses the app's teal accent.
const tabBtn = (active) => ({
  padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: "pointer",
  border: active ? "1.5px solid #0F6E56" : "1px solid #e2e6ea",
  background: active ? "#E7F6EC" : "#fff", color: active ? "#0F6E56" : "#5b6472",
});
const iso = (o = 0) => { const d = new Date(); d.setDate(d.getDate() + o); return d.toISOString().slice(0, 10); };
const emptyForm = () => ({
  name: "", contact_name: "", contact_phone: "", company_name: "", company_tin: "",
  lines: [{ room_type_id: "", quantity: 1 }],
  check_in_date: iso(0), check_out_date: iso(1), rate_per_night: "", notes: "",
});

export default function GroupBookingsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const role = getRole();
  const canEdit = role === "manager" || role === "front_desk";
  const cur = getCurrency() || "ETB";

  const [blocks, setBlocks] = useState(null);
  const [types, setTypes] = useState([]);
  const [partners, setPartners] = useState([]);
  // Inline "outsource to a partner hotel" panel state (per open block).
  const [outOpen, setOutOpen] = useState(false);
  const [oform, setOform] = useState(null);
  const [oerr, setOerr] = useState("");
  // Two simple tabs: "list" = existing groups, "new" = create form. Keeps the
  // page uncluttered for front-desk staff (create and browse are separate).
  const [tab, setTab] = useState("list");
  const [form, setForm] = useState(emptyForm());
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pay, setPay] = useState({ method: "cash", reference: "", amount: "" });
  const [gc, setGc] = useState({ description: "", amount: "" });
  const [memberEditId, setMemberEditId] = useState(null);
  const [mform, setMform] = useState({});
  // Block-header edit (#9) — company / TIN / contact / discount / notes.
  const [blockEdit, setBlockEdit] = useState(false);
  const [bform, setBform] = useState({});
  // Room availability for the open block's dates — drives the visual
  // RoomPicker so assigning a member a room shows the same colour-coded
  // popup used on individual bookings (green free, amber booked, etc.).
  const [avail, setAvail] = useState([]);
  const [openDates, setOpenDates] = useState(null);
  const [roomMsg, setRoomMsg] = useState("");
  const [availErr, setAvailErr] = useState(false);

  const reload = () => fetchBlocks().then(setBlocks).catch(() => setBlocks([]));
  useEffect(() => {
    reload();
    fetchRoomTypes().then(setTypes).catch(() => setTypes([]));
    fetchPartnerHotels().then(setPartners).catch(() => setPartners([]));
  }, []);

  // Map a partner_hotel_id → display name (for the outsourced placements list).
  const partnerName = (id) => {
    const p = (partners || []).find((x) => x.id === id);
    return p ? p.name : "—";
  };
  const emptyOutsource = (blk) => ({
    guest_name: "", partner_hotel: "", sell_rate: "", cost_rate: "",
    check_in_date: (blk && blk.check_in_date) || iso(0),
    check_out_date: (blk && blk.check_out_date) || iso(1),
    rooms: 1, note: "",
  });

  const loadAvail = (dates) => {
    if (!dates || !dates.ci || !dates.co) { setAvail([]); setAvailErr(false); return; }
    fetchRoomAvailability(dates.ci, dates.co, null, null)
      .then((r) => { setAvail(r || []); setAvailErr(false); })
      .catch(() => { setAvail([]); setAvailErr(true); });
  };
  const openBlock = async (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); setAvail([]); setOpenDates(null); setOutOpen(false); setOform(null); setOerr(""); setBlockEdit(false); return; }
    setOpenId(id); setDetail(null); setOutOpen(false); setOform(null); setOerr(""); setBlockEdit(false);
    const blk = (blocks || []).find((x) => x.id === id);
    let dates = blk ? { ci: blk.check_in_date, co: blk.check_out_date } : null;
    setOpenDates(dates); loadAvail(dates);
    try {
      const d = await fetchBlock(id); setDetail(d); setPay({ method: "cash", reference: "", amount: String(d.balance_due || 0) });
      // Authoritative dates from the detail — reload availability from them so
      // the room picker is never empty due to a stale/missing list date.
      dates = { ci: d.check_in_date, co: d.check_out_date };
      setOpenDates(dates); loadAvail(dates);
    } catch { setDetail(null); }
  };
  const refreshDetail = async () => { if (openId) { try { setDetail(await fetchBlock(openId)); } catch {} } loadAvail(openDates); reload(); };
  const assignRoom = async (mid, rid) => {
    setRoomMsg("");
    try {
      await updateReservation(mid, { room_id: rid ? Number(rid) : null });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setRoomMsg((d && typeof d === "string") ? d : t("room_taken"));
    }
    // Always re-sync so the picker reflects the true, current availability
    // (a sibling assignment may have just taken the room).
    refreshDetail();
  };
  const reinstateMember = async (mid) => {
    try { await reinstateReservation(mid); refreshDetail(); } catch { /* ignore */ }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { room_type_id: "", quantity: 1 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const toggleMember = (mem) => {
    if (memberEditId === mem.id) { setMemberEditId(null); return; }
    setMemberEditId(mem.id);
    setMform({
      guest_email: mem.guest_email || "", guest_phone: mem.guest_phone || "",
      origin: mem.origin || "",
      id_type: mem.id_type || "", id_number: mem.id_number || "",
      region: mem.region || "", city: mem.city || "", woreda: mem.woreda || "", kebele: mem.kebele || "",
    });
  };
  const saveMember = async (mid) => {
    try {
      await updateReservation(mid, {
        guest_email: mform.guest_email || null, guest_phone: mform.guest_phone || null,
        origin: mform.origin || null,
        id_type: mform.id_type || null, id_number: mform.id_number || null,
        region: mform.region || null, city: mform.city || null,
        woreda: mform.woreda || null, kebele: mform.kebele || null,
      });
      setMemberEditId(null); refreshDetail();
    } catch { /* ignore */ }
  };
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.name.trim()) { setErr(t("name_req")); return; }
    if (form.check_out_date <= form.check_in_date) { setErr(t("date_req")); return; }
    try {
      const b = await createBlock({
        name: form.name.trim(), contact_name: form.contact_name || null, contact_phone: form.contact_phone || null,
        company_name: form.company_name || null, company_tin: form.company_tin || null,
        lines: form.lines.map((l) => ({ room_type_id: l.room_type_id ? Number(l.room_type_id) : null, quantity: Number(l.quantity) || 1 })),
        check_in_date: form.check_in_date, check_out_date: form.check_out_date,
        rate_per_night: form.rate_per_night === "" ? null : Number(form.rate_per_night), notes: form.notes || null,
      });
      setForm(emptyForm()); setTab("list"); await reload();
      setOpenId(b.id); setDetail(b); setPay({ method: "cash", reference: "", amount: String(b.balance_due || 0) });
    } catch { setErr(t("could_not")); }
  };

  const toggleBlockEdit = () => {
    if (blockEdit) { setBlockEdit(false); return; }
    setBform({
      name: detail.name || "", company_name: detail.company_name || "", company_tin: detail.company_tin || "",
      contact_name: detail.contact_name || "", contact_phone: detail.contact_phone || "",
      discount_amount: detail.discount_amount != null ? String(detail.discount_amount) : "",
      notes: detail.notes || "",
    });
    setBlockEdit(true);
  };
  const saveBlock = async () => {
    if (!(bform.name || "").trim()) { setErr(t("name_req")); return; }
    try {
      const d = await updateBlock(openId, {
        name: bform.name.trim(),
        company_name: bform.company_name || null, company_tin: bform.company_tin || null,
        contact_name: bform.contact_name || null, contact_phone: bform.contact_phone || null,
        discount_amount: bform.discount_amount === "" ? null : Number(bform.discount_amount),
        notes: bform.notes || null,
      });
      setDetail(d); setBlockEdit(false); reload();
    } catch { setErr(t("could_not")); }
  };
  const addRoom = async () => { try { const d = await addBlockRoom(openId, {}); setDetail(d); reload(); } catch {} };
  const rmRoom = async (mid) => { try { const d = await removeBlockRoom(openId, mid); setDetail(d); reload(); } catch {} };
  const saveGuest = async (mid, nameVal) => { try { await updateReservation(mid, { guest_name: nameVal }); } catch {} };
  const doPay = async () => { try { const d = await blockPayment(openId, { method: pay.method, reference: pay.reference || null, amount: Number(pay.amount) || 0 }); setDetail(d); setPay({ ...pay, amount: String(d.balance_due || 0) }); reload(); } catch {} };
  const addGroupCharge = async () => {
    const amt = Number(gc.amount);
    if (!gc.description.trim() || !(amt > 0)) return;
    try {
      const d = await addBlockCharge(openId, { description: gc.description.trim(), amount: amt, source: "other" });
      setDetail(d); setGc({ description: "", amount: "" });
      setPay((p) => ({ ...p, amount: String(d.balance_due || 0) })); reload();
    } catch {}
  };
  const delGroupCharge = async (cid) => {
    try { const d = await deleteBlockCharge(openId, cid); setDetail(d); setPay((p) => ({ ...p, amount: String(d.balance_due || 0) })); reload(); } catch {}
  };
  const doCancel = async () => { if (!window.confirm(t("cancel_group_q"))) return; try { const d = await cancelBlock(openId); setDetail(d); reload(); } catch {} };
  // Bulk (in-advance) group check-in / check-out.
  const doBulkIn = async () => {
    try {
      const d = await blockCheckIn(openId); setDetail(d); reload();
      if (d.skipped_count) window.alert(t("bulk_skipped").replace("{n}", d.skipped_count));
    } catch {}
  };
  const doBulkOut = async () => {
    if (!window.confirm(t("bulk_out_q"))) return;
    try { const d = await blockCheckOut(openId); setDetail(d); reload(); } catch {}
  };
  const toggleOutsource = (blk) => {
    setOerr("");
    if (outOpen) { setOutOpen(false); setOform(null); return; }
    setOform(emptyOutsource(blk)); setOutOpen(true);
  };
  const submitOutsource = async () => {
    const o = oform || {};
    setOerr("");
    const name = (o.partner_hotel || "").trim();
    if (!name) { setOerr(t("partner_req")); return; }
    if (o.sell_rate === "" || o.cost_rate === "") { setOerr(t("rate_req")); return; }
    // If the typed name matches an existing partner (case-insensitive) send the id,
    // otherwise send the name so the backend auto-creates/reuses the partner hotel.
    const match = (partners || []).find((p) => (p.name || "").toLowerCase() === name.toLowerCase());
    const body = {
      ...(match ? { partner_hotel_id: match.id } : { partner_hotel_name: name }),
      ...(o.guest_name && o.guest_name.trim() ? { guest_name: o.guest_name.trim() } : {}),
      sell_rate: Number(o.sell_rate) || 0,
      cost_rate: Number(o.cost_rate) || 0,
      check_in_date: o.check_in_date,
      check_out_date: o.check_out_date,
      rooms: Number(o.rooms) || 1,
      ...(o.note && o.note.trim() ? { note: o.note.trim() } : {}),
    };
    try {
      await createBlockOutsource(openId, body);
      // Newly-created partner may not be in the cached list yet — refresh it too.
      fetchPartnerHotels().then(setPartners).catch(() => {});
      setOutOpen(false); setOform(null);
      refreshDetail();
    } catch (e) {
      setOerr((e && e.response && e.response.data && e.response.data.detail) || t("could_not"));
    }
  };
  const printReceipt = async () => {
    let d; try { d = await fetchBlockReceipt(openId); } catch { return; }
    const rc = d.hotel.currency || cur; const m = (v) => formatPrice(v || 0, rc);
    const h = d.hotel || {}; const cst = d.customer || {}; const blk = d.block || {};
    const L = { billto: { en: "Bill to", am: "ለ", om: "Kan kaffalu" }, subtotal: { en: "Subtotal", am: "ንዑስ ድምር", om: "Ida'ama xiqqaa" } };
    const tr2 = (k) => (L[k] && (L[k][lang] || L[k].en)) || k;
    const hasTax = (d.tax_amount || d.vat_amount || 0) > 0;
    const idLine = [h.tin ? `TIN: ${h.tin}` : "", h.vat_reg_no ? `VAT: ${h.vat_reg_no}` : ""].filter(Boolean).join(" · ");
    const invNo = d.invoice_no ? `No. ${d.invoice_no}` : "";
    const rows = (d.lines || []).map((l) => `<tr><td>${l.description}</td><td class="r">${m(l.amount)}</td></tr>`).join("");
    const discLine = d.discount > 0 ? `<tr><td>${t("cancel")}</td><td class="r">-${m(d.discount)}</td></tr>` : "";
    const netLine = hasTax ? `<tr><td>${tr2("subtotal")}</td><td class="r">${m(d.subtotal_net)}</td></tr>` : "";
    const taxLine = hasTax ? `<tr><td>${d.tax_label || "Tax"}</td><td class="r">${m(d.tax_amount || d.vat_amount)}</td></tr>` : "";
    const custTin = cst.tin ? `<div class="muted">TIN: ${cst.tin}</div>` : "";
    const html = `<html><head><meta charset="utf-8"><title>${h.legal_name || h.name || ""} — ${d.doc_title || "Invoice"}</title><style>
      body{font-family:Arial,sans-serif;max-width:360px;margin:0 auto;padding:16px;color:#111}
      h2{margin:0;font-size:17px}.muted{color:#666;font-size:11.5px}
      .head{text-align:center}.title{text-align:center;font-weight:700;font-size:14px;letter-spacing:.5px;margin:8px 0 2px}
      table{width:100%;border-collapse:collapse;margin-top:6px;font-size:12.5px}td{padding:3px 0;vertical-align:top}
      .r{text-align:right}.tot td{border-top:1px solid #999;font-weight:700;padding-top:6px}.hr{border-top:1px dashed #999;margin:8px 0}
      </style></head><body>
      <div class="head"><h2>${h.legal_name || h.name || ""}</h2>
        ${h.address ? `<div class="muted">${h.address}</div>` : ""}
        ${h.phone ? `<div class="muted">${h.phone}</div>` : ""}
        ${idLine ? `<div class="muted">${idLine}</div>` : ""}</div>
      <div class="title">${d.doc_title || "Invoice"}</div>
      <div class="muted head">${invNo}${d.issued_at ? (invNo ? " · " : "") + new Date(d.issued_at).toLocaleString() : ""}</div>
      <div class="hr"></div>
      <div class="muted"><b>${tr2("billto")}:</b> ${cst.name || blk.company_name || blk.name || ""}</div>
      ${custTin}
      <div class="muted">${blk.check_in_date || ""} → ${blk.check_out_date || ""}</div>
      <table>${rows}${discLine}${netLine}${taxLine}<tr class="tot"><td>${t("total")}</td><td class="r">${m(d.grand_total)}</td></tr></table>
      <div class="hr"></div><table><tr><td>${t("paid")}</td><td class="r">${m(d.paid)}</td></tr>
      ${d.balance_due > 0 ? `<tr><td>${t("balance")}</td><td class="r">${m(d.balance_due)}</td></tr>` : ""}</table>
      </body></html>`;
    const w = window.open("", "_blank", "width=400,height=680"); if (!w) return;
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => { try { w.print(); } catch (e) {} }, 300);
  };

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2", boxSizing: "border-box" };

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      <div>
        <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
        <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>
      </div>

      {/* Tabs — keep the group list and the create form on separate screens. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button type="button" onClick={() => setTab("list")} style={tabBtn(tab === "list")}>{t("tab_groups")}</button>
        {canEdit && <button type="button" onClick={() => setTab("new")} style={tabBtn(tab === "new")}>{t("tab_new_group")}</button>}
      </div>

      {tab === "new" && canEdit && (
        <form onSubmit={submit} style={{ ...CARD, padding: 16, marginTop: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            <input style={inp} placeholder={t("name") + " *"} value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
            <input style={inp} placeholder={t("company")} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
            <input style={inp} placeholder={t("company_tin")} value={form.company_tin} onChange={(e) => set("company_tin", e.target.value)} />
            <input style={inp} placeholder={t("contact")} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
            <input style={inp} placeholder={t("phone")} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} />
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("check_in")}
              <input type="date" style={{ ...inp, width: "100%", marginTop: 3 }} value={form.check_in_date} onChange={(e) => set("check_in_date", e.target.value)} /></label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("check_out")}
              <input type="date" style={{ ...inp, width: "100%", marginTop: 3 }} value={form.check_out_date} onChange={(e) => set("check_out_date", e.target.value)} /></label>
            <input style={inp} type="number" min="0" placeholder={t("rate")} value={form.rate_per_night} onChange={(e) => set("rate_per_night", e.target.value)} />
            <input style={inp} placeholder={t("notes")} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#5f6b78", marginBottom: 6 }}>{t("room_types_h")}</div>
            {form.lines.map((ln, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center", flexWrap: "wrap" }}>
                <select style={{ ...inp, flex: 1, minWidth: 160 }} value={ln.room_type_id} onChange={(e) => setLine(i, "room_type_id", e.target.value)}>
                  <option value="">{t("pick_type")}</option>
                  {types.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}{tp.base_rate != null ? ` — ${formatPrice(tp.base_rate, cur)}` : ""}</option>)}
                </select>
                <input type="number" min="1" style={{ ...inp, width: 90 }} placeholder={t("qty")} value={ln.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                {form.lines.length > 1 && <button type="button" onClick={() => removeLine(i)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 16 }}>×</button>}
              </div>
            ))}
            <button type="button" className="btn secondary small" onClick={addLine}>+ {t("add_type")}</button>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button className="btn primary small" type="submit">{t("create")}</button>
            <button className="btn secondary small" type="button" onClick={() => setTab("list")}>{t("cancel")}</button>
            {err && <span style={{ color: "#A32D2D", fontSize: 13 }}>{err}</span>}
          </div>
        </form>
      )}

      {tab === "list" && (blocks == null ? (
        <p className="muted" style={{ marginTop: 16 }}>…</p>
      ) : blocks.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 16, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {blocks.map((b) => {
            const m = STMETA[b.status] || STMETA.open;
            const open = openId === b.id;
            return (
              <div key={b.id} style={{ ...CARD, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", cursor: "pointer" }} onClick={() => openBlock(b.id)}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, fontSize: 16 }}>{b.name}</span>
                      <span style={{ background: m.bg, color: m.fg, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{t("st_" + b.status)}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{b.company_name ? b.company_name + " · " : ""}{b.rooms} {t("rooms")} · {b.check_in_date} → {b.check_out_date}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: "#27500A" }}>{formatPrice(b.total, cur)}</div>
                    {b.balance_due > 0 && <div style={{ fontSize: 12, color: "#8A5A0B" }}>{t("balance")}: {formatPrice(b.balance_due, cur)}</div>}
                  </div>
                </div>

                {open && detail && (
                  <div style={{ marginTop: 12, borderTop: "1px solid #eef2f2", paddingTop: 12 }}>
                    {/* Block-header edit (#9): company / TIN / contact / discount / notes. */}
                    {canEdit && detail.status !== "cancelled" && (
                      blockEdit ? (
                        <div style={{ marginBottom: 12, padding: 12, background: "#f7f9fa", borderRadius: 10, border: "1px solid #eef2f2" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                            <input style={inp} placeholder={t("name") + " *"} value={bform.name} onChange={(e) => setBform({ ...bform, name: e.target.value })} />
                            <input style={inp} placeholder={t("company")} value={bform.company_name} onChange={(e) => setBform({ ...bform, company_name: e.target.value })} />
                            <input style={inp} placeholder={t("company_tin")} value={bform.company_tin} onChange={(e) => setBform({ ...bform, company_tin: e.target.value })} />
                            <input style={inp} placeholder={t("contact")} value={bform.contact_name} onChange={(e) => setBform({ ...bform, contact_name: e.target.value })} />
                            <input style={inp} placeholder={t("phone")} value={bform.contact_phone} onChange={(e) => setBform({ ...bform, contact_phone: e.target.value })} />
                            <input style={inp} type="number" min="0" placeholder={t("discount")} value={bform.discount_amount} onChange={(e) => setBform({ ...bform, discount_amount: e.target.value })} />
                            <input style={inp} placeholder={t("notes")} value={bform.notes} onChange={(e) => setBform({ ...bform, notes: e.target.value })} />
                          </div>
                          <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
                            <button className="btn primary small" onClick={saveBlock}>{t("save")}</button>
                            <button className="btn secondary small" onClick={() => { setBlockEdit(false); setErr(""); }}>{t("cancel")}</button>
                            {err && <span style={{ color: "#A32D2D", fontSize: 13 }}>{err}</span>}
                          </div>
                        </div>
                      ) : (
                        <button className="btn secondary small" style={{ marginBottom: 12 }} onClick={toggleBlockEdit}>✎ {t("edit_group")}</button>
                      )
                    )}
                    {/* member rooms */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(detail.members || []).filter((mem) => !mem.external).map((mem) => {
                        const sm = STMETA[mem.status] || STMETA.booked;
                        const editing = memberEditId === mem.id;
                        return (
                          <div key={mem.id}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <input defaultValue={mem.guest_name} placeholder={t("guest")} onBlur={(e) => saveGuest(mem.id, e.target.value)} style={{ ...inp, flex: 1, minWidth: 150, padding: 6 }} disabled={!canEdit} />
                              {canEdit && mem.status === "booked" ? (
                                <div style={{ width: 150, flex: "0 0 auto" }}>
                                  <RoomPicker t={t} rooms={avail} value={mem.room_id || ""} placeholder={mem.room_name || t("assign_room")} onChange={(rid) => assignRoom(mem.id, rid)} />
                                </div>
                              ) : (
                                <span className="muted" style={{ fontSize: 13, minWidth: 56 }}>{mem.room_name || "—"}</span>
                              )}
                              <span style={{ background: sm.bg, color: sm.fg, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{t("st_" + mem.status)}</span>
                              {canEdit && (mem.status === "no_show" || mem.status === "cancelled") && <button onClick={() => reinstateMember(mem.id)} style={{ background: "none", border: "none", color: "#1D7A44", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t("reinstate")}</button>}
                              {canEdit && <button onClick={() => toggleMember(mem)} style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontSize: 12 }}>{t("details")}</button>}
                              {canEdit && mem.status === "booked" && <button onClick={() => rmRoom(mem.id)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 15 }}>×</button>}
                            </div>
                            {editing && (
                              <div style={{ margin: "6px 0 10px", padding: 10, background: "#f7f9fa", borderRadius: 8 }}>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                                  <input style={inp} placeholder={t("origin")} value={mform.origin} onChange={(e) => setMform({ ...mform, origin: e.target.value })} />
                                  <input style={inp} placeholder={t("email")} value={mform.guest_email} onChange={(e) => setMform({ ...mform, guest_email: e.target.value })} />
                                  <input style={inp} placeholder={t("phone")} value={mform.guest_phone} onChange={(e) => setMform({ ...mform, guest_phone: e.target.value })} />
                                  <select style={inp} value={mform.id_type} onChange={(e) => setMform({ ...mform, id_type: e.target.value })}>
                                    <option value="">{t("id_type")}</option>
                                    <option value="kebele">{t("id_kebele")}</option>
                                    <option value="fayda">{t("id_fayda")}</option>
                                    <option value="passport">{t("id_passport")}</option>
                                    <option value="other">{t("id_other")}</option>
                                  </select>
                                  <input style={inp} placeholder={t("id_number")} value={mform.id_number} onChange={(e) => setMform({ ...mform, id_number: e.target.value })} />
                                  <input style={inp} placeholder={t("region")} value={mform.region} onChange={(e) => setMform({ ...mform, region: e.target.value })} />
                                  <input style={inp} placeholder={t("city")} value={mform.city} onChange={(e) => setMform({ ...mform, city: e.target.value })} />
                                  <input style={inp} placeholder={t("woreda")} value={mform.woreda} onChange={(e) => setMform({ ...mform, woreda: e.target.value })} />
                                  <input style={inp} placeholder={t("kebele")} value={mform.kebele} onChange={(e) => setMform({ ...mform, kebele: e.target.value })} />
                                </div>
                                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                                  <button className="btn primary small" onClick={() => saveMember(mem.id)}>{t("save")}</button>
                                  <button className="btn secondary small" onClick={() => setMemberEditId(null)}>{t("cancel")}</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {(detail.members || []).some((m) => !m.external) && avail.length === 0 && (
                      <div style={{ fontSize: 12, marginTop: 6, color: availErr ? "#A32D2D" : "#8A5A0B" }}>{availErr ? t("avail_err") : t("no_rooms_hint")}</div>
                    )}
                    {roomMsg && <div style={{ color: "#A32D2D", fontSize: 12, marginTop: 6 }}>{roomMsg}</div>}
                    {canEdit && detail.status !== "cancelled" && <button className="btn secondary small" style={{ marginTop: 8 }} onClick={addRoom}>+ {t("add_room")}</button>}
                    {canEdit && detail.status !== "cancelled" && <button className="btn secondary small" style={{ marginTop: 8, marginLeft: 8 }} onClick={() => toggleOutsource(b)}>{t("outsource")}</button>}
                    {canEdit && detail.status !== "cancelled" && (detail.members || []).some((m) => m.status === "booked") &&
                      <button className="btn primary small" style={{ marginTop: 8, marginLeft: 8 }} onClick={doBulkIn}>🔑 {t("bulk_in")}</button>}
                    {canEdit && detail.status !== "cancelled" && (detail.members || []).some((m) => m.status === "checked_in") &&
                      <button className="btn primary small" style={{ marginTop: 8, marginLeft: 8 }} onClick={doBulkOut}>🚪 {t("bulk_out")}</button>}
                    <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{t("tip")}</div>

                    {/* Outsource-to-partner-hotel inline form */}
                    {outOpen && oform && (
                      <div style={{ margin: "10px 0", padding: 12, background: "#f7f9fa", borderRadius: 10, border: "1px solid #eef2f2" }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{t("outsource")}</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                          <input style={inp} placeholder={t("guest")} value={oform.guest_name} onChange={(e) => setOform({ ...oform, guest_name: e.target.value })} />
                          <input style={inp} list="partner-hotels" placeholder={t("partner_hotel") + " *"} value={oform.partner_hotel} onChange={(e) => setOform({ ...oform, partner_hotel: e.target.value })} />
                          <datalist id="partner-hotels">
                            {partners.map((p) => <option key={p.id} value={p.name} />)}
                          </datalist>
                          <input style={inp} type="number" min="0" placeholder={t("sell_rate") + " *"} value={oform.sell_rate} onChange={(e) => setOform({ ...oform, sell_rate: e.target.value })} />
                          <input style={inp} type="number" min="0" placeholder={t("cost_rate") + " *"} value={oform.cost_rate} onChange={(e) => setOform({ ...oform, cost_rate: e.target.value })} />
                          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("check_in")}
                            <input type="date" style={{ ...inp, width: "100%", marginTop: 3 }} value={oform.check_in_date} onChange={(e) => setOform({ ...oform, check_in_date: e.target.value })} /></label>
                          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("check_out")}
                            <input type="date" style={{ ...inp, width: "100%", marginTop: 3 }} value={oform.check_out_date} onChange={(e) => setOform({ ...oform, check_out_date: e.target.value })} /></label>
                          <input style={inp} type="number" min="1" placeholder={t("rooms")} value={oform.rooms} onChange={(e) => setOform({ ...oform, rooms: e.target.value })} />
                          <input style={inp} placeholder={t("note")} value={oform.note} onChange={(e) => setOform({ ...oform, note: e.target.value })} />
                        </div>
                        <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
                          <button className="btn primary small" onClick={submitOutsource}>{t("add_outsource")}</button>
                          <button className="btn secondary small" onClick={() => { setOutOpen(false); setOform(null); setOerr(""); }}>{t("cancel")}</button>
                          {oerr && <span style={{ color: "#A32D2D", fontSize: 13 }}>{oerr}</span>}
                        </div>
                      </div>
                    )}

                    {/* Outsourced placements (external members) */}
                    {(detail.members || []).some((mem) => mem.external) && (
                      <div style={{ marginTop: 12, borderTop: "1px solid #eef2f2", paddingTop: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{t("outsourced_placements")}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          {(detail.members || []).filter((mem) => mem.external).map((mem) => {
                            const sell = mem.rate_per_night || 0;
                            const cost = mem.partner_cost_rate || 0;
                            const editing = memberEditId === mem.id;
                            return (
                              <div key={mem.id}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 13, flexWrap: "wrap" }}>
                                  <span style={{ display: "flex", alignItems: "center", gap: 6, flex: "1 1 220px", minWidth: 180 }}>
                                    <input defaultValue={mem.guest_name} placeholder={t("guest")} onBlur={(e) => saveGuest(mem.id, e.target.value)} style={{ ...inp, flex: 1, minWidth: 120, padding: 6 }} disabled={!canEdit} />
                                    <span className="muted">· {partnerName(mem.partner_hotel_id)}</span>
                                  </span>
                                  <span style={{ textAlign: "right" }}>
                                    {t("sell_rate")}: <b>{formatPrice(sell, cur)}</b>
                                    <span className="muted"> · {t("cost_rate")}: {formatPrice(cost, cur)}</span>
                                    <span style={{ color: "#1D7A44" }}> · {t("margin")}: {formatPrice(sell - cost, cur)}</span>
                                    {canEdit && <button onClick={() => toggleMember(mem)} style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontSize: 12, marginLeft: 8 }}>{t("details")}</button>}
                                  </span>
                                </div>
                                {editing && (
                                  <div style={{ margin: "6px 0 10px", padding: 10, background: "#f7f9fa", borderRadius: 8 }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                                      <input style={inp} placeholder={t("origin")} value={mform.origin} onChange={(e) => setMform({ ...mform, origin: e.target.value })} />
                                      <input style={inp} placeholder={t("email")} value={mform.guest_email} onChange={(e) => setMform({ ...mform, guest_email: e.target.value })} />
                                      <input style={inp} placeholder={t("phone")} value={mform.guest_phone} onChange={(e) => setMform({ ...mform, guest_phone: e.target.value })} />
                                      <select style={inp} value={mform.id_type} onChange={(e) => setMform({ ...mform, id_type: e.target.value })}>
                                        <option value="">{t("id_type")}</option>
                                        <option value="kebele">{t("id_kebele")}</option>
                                        <option value="fayda">{t("id_fayda")}</option>
                                        <option value="passport">{t("id_passport")}</option>
                                        <option value="other">{t("id_other")}</option>
                                      </select>
                                      <input style={inp} placeholder={t("id_number")} value={mform.id_number} onChange={(e) => setMform({ ...mform, id_number: e.target.value })} />
                                      <input style={inp} placeholder={t("region")} value={mform.region} onChange={(e) => setMform({ ...mform, region: e.target.value })} />
                                      <input style={inp} placeholder={t("city")} value={mform.city} onChange={(e) => setMform({ ...mform, city: e.target.value })} />
                                      <input style={inp} placeholder={t("woreda")} value={mform.woreda} onChange={(e) => setMform({ ...mform, woreda: e.target.value })} />
                                      <input style={inp} placeholder={t("kebele")} value={mform.kebele} onChange={(e) => setMform({ ...mform, kebele: e.target.value })} />
                                    </div>
                                    <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                                      <button className="btn primary small" onClick={() => saveMember(mem.id)}>{t("save")}</button>
                                      <button className="btn secondary small" onClick={() => setMemberEditId(null)}>{t("cancel")}</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* linked functions (events) — part of the consolidated bill */}
                    {(detail.events || []).length > 0 && (
                      <div style={{ marginTop: 12, borderTop: "1px solid #eef2f2", paddingTop: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{t("linked_events")}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          {detail.events.map((ev) => (
                            <div key={ev.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, fontSize: 13 }}>
                              <span>{ev.name}{ev.event_date ? <span className="muted"> · {ev.event_date}</span> : null}</span>
                              <span style={{ textAlign: "right" }}>
                                <b>{formatPrice(ev.total, cur)}</b>
                                {ev.balance > 0 && <span style={{ color: "#8A5A0B" }}> · {t("balance")} {formatPrice(ev.balance, cur)}</span>}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{t("events_note")}</div>
                      </div>
                    )}

                    {/* group charges — shared extras on the master bill */}
                    <div style={{ marginTop: 12, borderTop: "1px solid #eef2f2", paddingTop: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{t("group_charges")}</div>
                      {(detail.block_charges || []).length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          {detail.block_charges.map((c) => (
                            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, fontSize: 13 }}>
                              <span>{c.description}</span>
                              <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                                <b>{formatPrice(c.amount, cur)}</b>
                                {canEdit && detail.status !== "cancelled" && <button onClick={() => delGroupCharge(c.id)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 15, lineHeight: 1 }}>×</button>}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {canEdit && detail.status !== "cancelled" && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 8 }}>
                          <input style={{ ...inp, flex: 1, minWidth: 160 }} placeholder={t("gc_desc")} value={gc.description} onChange={(e) => setGc({ ...gc, description: e.target.value })} />
                          <input style={{ ...inp, width: 120 }} type="number" min="0" placeholder={t("amount")} value={gc.amount} onChange={(e) => setGc({ ...gc, amount: e.target.value })} />
                          <button className="btn secondary small" onClick={addGroupCharge}>+ {t("gc_add")}</button>
                        </div>
                      )}
                      <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{t("group_charges_note")}</div>
                    </div>

                    {/* totals */}
                    <div style={{ marginTop: 12, fontSize: 14 }}>
                      {detail.own_accommodation_total != null && <div style={{ display: "flex", justifyContent: "space-between", color: "#5b6472" }}><span>{t("own_accommodation")}</span><span>{formatPrice(detail.own_accommodation_total, cur)}</span></div>}
                      {detail.outsourced_total != null && <div style={{ display: "flex", justifyContent: "space-between", color: "#5b6472" }}><span>{t("outsourced_accommodation")}</span><span>{formatPrice(detail.outsourced_total, cur)}</span></div>}
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span>{t("total")}</span><b>{formatPrice(detail.total, cur)}</b></div>
                      <div style={{ display: "flex", justifyContent: "space-between", color: "#5b6472" }}><span>{t("paid")}</span><span>{formatPrice(detail.paid_total != null ? detail.paid_total : (detail.paid_amount || 0), cur)}</span></div>
                      {detail.balance_due > 0 && <div style={{ display: "flex", justifyContent: "space-between", color: "#8A5A0B", fontWeight: 700 }}><span>{t("balance")}</span><span>{formatPrice(detail.balance_due, cur)}</span></div>}
                    </div>

                    {/* master payment */}
                    {canEdit && detail.status !== "cancelled" && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t("master_pay")}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                          <select style={inp} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                            <option value="cash">{t("pm_cash")}</option>
                            <option value="telebirr">{t("pm_telebirr")}</option>
                            <option value="cbe_birr">{t("pm_cbe_birr")}</option>
                            <option value="pos">{t("pm_pos")}</option>
                            <option value="other">{t("pm_other")}</option>
                          </select>
                          <input style={inp} placeholder={t("reference")} value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} />
                          <input style={{ ...inp, width: 120 }} type="number" min="0" placeholder={t("amount")} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
                          <button className="btn primary small" onClick={doPay}>{t("take_pay")}</button>
                        </div>
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 14, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <button onClick={printReceipt} style={{ fontSize: 13, background: "none", border: "none", color: "#3C3489", cursor: "pointer", padding: 0 }}>{t("receipt")}</button>
                      {canEdit && detail.status !== "cancelled" && <button onClick={doCancel} style={{ fontSize: 13, background: "none", border: "none", color: "#A32D2D", cursor: "pointer", padding: 0 }}>{t("cancel_group")}</button>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
